import {
  DeviceCode,
  type DeviceStatus as FHIRDeviceStatus,
} from '@quromedical/fhir-common';
import type { EnforceExtends } from '@quromedical/utils';
import { type ZodString, z } from 'zod';
import { PatchPostureCalibrateValueSchema } from '../device-command/schema';
import { UUIDSchema } from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import { PaginatedBundleSchema } from '../domain/schema';

import type { WithId } from '../types';

const deviceStatus = [
  'active',
  'inactive',
  'entered-in-error',
  'unknown',
] as const;
export const DeviceStatusSchema = z
  .enum(deviceStatus)
  .describe('The status of the device');
export const DeviceCodeSchema = z
  .nativeEnum(DeviceCode)
  .describe('The code of the device');

const RelayNameRegex =
  /^((samsung-a(12|13|14|15|16|24))-[0-9]{1,3}|friendlyelec-nanopim4b-[0-9]{1,3})$/;
const RelayNameSchema = z
  .string()
  .regex(RelayNameRegex)
  .describe('The name of the relay device');

export const PatchNameRegex = /^VC2B008BF_[0-9-A-Z]{6}$/;
const PatchNameSchema = z
  .string()
  .regex(PatchNameRegex)
  .describe('The name of the patch device');

const BandNameRegex = /^[0-9]{7}$/;
const BandNameSchema = z
  .string()
  .regex(BandNameRegex)
  .describe('The name of the vital band device');

const GlucoseDeviceNameRegex = /^(F).{11}$/;
const GlucoseDeviceNameSchema = z
  .string()
  .regex(GlucoseDeviceNameRegex)
  .describe('The name of the glucose device');

const BloodPressureSchema = z
  .string()
  .length(5)
  .describe('The name of the blood pressure device');

const patchNameError =
  'A patch name should start with VC2B008BF_ and have a total of 16 characters.';

const bandNameError = 'A band name should have 7 digits.';

const relayNameError =
  'A relay name should start with samsung-a12, samsung-a13, samsung-a14, samsung-a24 or friendlyelec-nanopim4b and end with 1, 2 or 3 digits.';

const glucoseNameError =
  'A glucose name should start with F and 11 characters long. ';

export const DeviceSchemaMap: Record<
  DeviceCode,
  { schema: ZodString; message?: string }
> = {
  [DeviceCode.relay]: {
    schema: RelayNameSchema,
    message: relayNameError,
  },
  [DeviceCode.patch]: {
    schema: PatchNameSchema,
    message: patchNameError,
  },
  [DeviceCode.band]: {
    schema: BandNameSchema,
    message: bandNameError,
  },
  [DeviceCode.glucose]: {
    schema: GlucoseDeviceNameSchema,
    message: glucoseNameError,
  },
  [DeviceCode.bloodPressure]: {
    schema: BloodPressureSchema,
  },
};

type SupportedDeviceStatus = (typeof deviceStatus)[number];

export type DeviceStatus = EnforceExtends<
  FHIRDeviceStatus,
  SupportedDeviceStatus
>;

const BaseDeviceSchema = z.object({
  name: z.string(),
  type: DeviceCodeSchema,
  status: DeviceStatusSchema,
  serialNumber: z.string().optional(),
});

const deviceNameRefinement = (
  obj: { name?: string; type?: DeviceCode; serialNumber?: string },
  ctx: z.RefinementCtx,
) => {
  if (!obj.name && !obj.type) {
    return;
  }

  if (obj.type === DeviceCode.relay) {
    if (!obj.serialNumber) {
      ctx.addIssue({
        code: 'custom',
        message: 'Serial number is required',
        path: ['serialNumber'],
      });
      return;
    }
  }

  if (!obj.type || !obj.name) {
    ctx.addIssue({
      code: 'custom',
      message: 'You must provide both name and type',
      path: ['name'],
    });
    return;
  }
  // This expression removes all instances of \n, \t, \r, and \" from the name.
  const name = obj.name.replace(/\\[ntr"]/g, '');

  const { schema, message } = DeviceSchemaMap[obj.type];
  const parsed = schema.safeParse(name);
  if (!parsed.success) {
    ctx.addIssue({
      code: 'custom',
      message: message || parsed.error.message,
      path: ['name'],
    });
  }
};

export const CreateDeviceSchema =
  BaseDeviceSchema.superRefine(deviceNameRefinement);

export const CreateBulkDevicesSchema = z.object({
  devices: CreateDeviceSchema.array(),
});

export const CreateBulkDevicesResponseSchema = CreateDeviceSchema.array();

export const DeviceSchema = BaseDeviceSchema.extend({
  id: UUIDSchema,
});

export const DeviceGetByIdSchema = DeviceSchema.optional();

export const UpdateDeviceSchema = z
  .object({
    id: UUIDSchema,
    name: z.string().optional(),
    type: DeviceCodeSchema.optional(),
    status: DeviceStatusSchema.optional(),
    serialNumber: z.string().optional(),
  })
  .superRefine(deviceNameRefinement);

export type Device = z.infer<typeof DeviceSchema>;

export type CreateDeviceRequest = z.infer<typeof CreateDeviceSchema>;
export type UpdateDeviceRequest = z.infer<typeof UpdateDeviceSchema>;

// @deprecated Use `Device` instead
export type DeviceGetResponse = Device;
// @deprecated Use `CreateDeviceRequest` instead
export type DeviceCreateRequest = CreateDeviceRequest;
// @deprecated Use `UpdateDeviceRequest` instead
export type DeviceUpdateRequest = CreateDeviceRequest;
// @deprecated Use `Device` instead
export type DeviceCreateResponse = WithId<Partial<Device>>;
// @deprecated Use `Device` instead
export type DeviceUpdateResponse = WithId<Partial<Device>>;

export type GetDevicesResponse = z.infer<typeof DeviceSchema>;

export const DeviceGetAllResponseSchema = PaginatedBundleSchema.extend({
  devices: DeviceSchema.array(),
});
export type DeviceGetAllResponse = z.infer<typeof DeviceGetAllResponseSchema>;

export const DeviceSearchSchema = PaginationSchema.extend({
  id: z.string().optional().describe('The id of the devices'),
  sort: z.string().optional().describe('The field to sort by'),
  'device-name': z.string().optional().describe('The name of the device'),
  type: z.nativeEnum(DeviceCode).optional().describe('The type of the device'),
  identifier: z
    .nativeEnum(DeviceCode)
    .optional()
    .describe('The type of the device'),
  status: z.string().optional().describe('The status of the device'),
  serialNumber: z
    .string()
    .optional()
    .describe('The serial number of the device'),
});

export type DeviceSearch = z.infer<typeof DeviceSearchSchema>;
export type CreateBulkDevicesRequest = z.infer<typeof CreateBulkDevicesSchema>;
export type CreateBulkDevicesResponse = z.infer<
  typeof CreateBulkDevicesResponseSchema
>;

// @deprecated Use `DeviceSearch` instead
export type DeviceGetAllParams = DeviceSearch;

export const patchCalibrateRequestSchema = z.object({
  id: UUIDSchema,
  posture: PatchPostureCalibrateValueSchema.optional(),
  temperature: z.number().optional(),
});
export const PatchCalibrateResponseSchema = z.object({});

export type PatchCalibrateRequest = z.infer<typeof patchCalibrateRequestSchema>;
export type PatchCalibrateResponse = z.infer<
  typeof PatchCalibrateResponseSchema
>;
